/**
 * The frames pinned to the desktop (plan 12 §4): one record per frame, kept
 * per account on this device (`accountScopedStorage`, not synced; the last
 * account's records go when the account changes). Only the owner overlay
 * writes them (`desktop-pin-actions.ts`, `desktop-pin-owner.ts`); every
 * window reads them live through the `storage` event.
 */
import { useSyncExternalStore } from "react";
import type { PinnedWidgetSize } from "@/components/shapes/shared/widget-sizing";
import { accountScopedStorage } from "@/lib/account-scoped-storage";
import type { PinRect } from "./desktop-pin-contract";
import {
  isPinnedWidgetSize,
  nearestWidgetSize,
  pinRectAtSize,
} from "./desktop-pin-resize";

export interface DesktopPinRecord {
  frameId: string;
  projectId: string;
  /** The display the pin was placed on, when known. */
  displayId: number | null;
  /**
   * The widget's size (plan 12 §8): `rect` is exactly that widget size, or
   * free, its own size.
   */
  size: PinnedWidgetSize;
  rect: PinRect;
}

const storage = accountScopedStorage("denker.desktop-pins.v1:");
const NONE: readonly DesktopPinRecord[] = [];

/**
 * The parsed pins, read from storage again only after it changed. The
 * invalidation subscribes first, so every other subscriber reads fresh.
 */
let snapshot: readonly DesktopPinRecord[] | null = null;
storage.subscribe(() => {
  snapshot = null;
});

/** Finite logical-point numbers in every field. */
export function isPinRect(value: unknown): value is PinRect {
  if (!value || typeof value !== "object") return false;
  const rect = value as Record<string, unknown>;
  return ["x", "y", "width", "height"].every((key) =>
    Number.isFinite(rect[key]),
  );
}

/**
 * A stored record, its rect exactly its size. A round-1 record (free size,
 * no `size`) takes the nearest widget size, its top-left kept.
 */
function toRecord(value: unknown): DesktopPinRecord | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const { frameId, projectId, displayId, rect } = record;
  if (
    typeof frameId !== "string" ||
    typeof projectId !== "string" ||
    !(displayId === null || typeof displayId === "number") ||
    !isPinRect(rect)
  ) {
    return null;
  }
  const size = isPinnedWidgetSize(record.size)
    ? record.size
    : nearestWidgetSize(rect);
  return {
    frameId,
    projectId,
    displayId,
    size,
    rect: pinRectAtSize(rect, size),
  };
}

function parse(raw: string | null): readonly DesktopPinRecord[] {
  if (!raw) return NONE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return NONE;
    return parsed
      .map(toRecord)
      .filter((record): record is DesktopPinRecord => record !== null);
  } catch {
    return NONE;
  }
}

/** The pins of the signed-in account; the same array until they change. */
export function readDesktopPins(): readonly DesktopPinRecord[] {
  snapshot ??= parse(storage.read());
  return snapshot;
}

export function desktopPinFor(frameId: string): DesktopPinRecord | undefined {
  return readDesktopPins().find((pin) => pin.frameId === frameId);
}

/** Pin changes here or in another window, and account changes. */
export function subscribeDesktopPins(listener: () => void): () => void {
  return storage.subscribe(listener);
}

/** Add or replace a frame's pin. Owner only. */
export function putDesktopPin(record: DesktopPinRecord): void {
  const others = readDesktopPins().filter(
    (pin) => pin.frameId !== record.frameId,
  );
  storage.write(JSON.stringify([...others, record]));
}

/** Drop a frame's pin; false when it had none. Owner only. */
export function removeDesktopPin(frameId: string): boolean {
  const pins = readDesktopPins();
  const kept = pins.filter((pin) => pin.frameId !== frameId);
  if (kept.length === pins.length) return false;
  storage.write(JSON.stringify(kept));
  return true;
}

/** The pins, live across this window's writes and other windows'. */
export function useDesktopPins(): readonly DesktopPinRecord[] {
  return useSyncExternalStore(
    subscribeDesktopPins,
    readDesktopPins,
    () => NONE,
  );
}
